import React from "react";
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Instagram,
  Facebook,
  ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const fadeInUp = {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 0.5 },
  };

  // ✅ Fonction optimisée pour naviguer vers Contact et scroller vers le formulaire
  const handleContactClick = () => {
    // Si on est déjà sur la page contact, juste scroller
    if (location.pathname === '/contact') {
      scrollToContactForm();
    } else {
      // Sinon, naviguer puis scroller
      navigate('/contact');
      setTimeout(() => {
        scrollToContactForm();
      }, 100);
    }
  };

  // ✅ Fonction de scroll vers le formulaire avec types TypeScript corrects
  const scrollToContactForm = () => {
    // Méthode 1 : Chercher par ID (le plus précis)
    const formById = document.getElementById('contact-form') as HTMLElement | null;
    if (formById) {
      const offsetTop = formById.offsetTop - 100; // 100px d'offset pour le header
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      return;
    }

    // Méthode 2 : Chercher la section du formulaire avec cast HTMLElement
    const formSection = (document.querySelector('section.bg-gray-50') || // Section avec bg gris
                        document.querySelector('.card') || // Le formulaire est dans une card
                        document.querySelector('form')) as HTMLElement | null; // Cast vers HTMLElement

    if (formSection) {
      const offsetTop = formSection.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      return;
    }

    // Méthode 3 : Fallback - scroll vers le milieu de la page
    const pageHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const scrollTarget = Math.max(300, (pageHeight - windowHeight) * 0.3); // 30% de la page
    
    window.scrollTo({
      top: scrollTarget,
      behavior: 'smooth'
    });
  };

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "contact@neoqualis.com",
      href: "mailto:contact@neoqualis.com",
    },
    {
      icon: Phone,
      label: "Téléphone",
      value: "+33 1 84 88 40 85", // ✅ Numéro harmonisé
      href: "tel:+33184884085",
    },
    {
      icon: MapPin,
      label: "Adresse",
      value: "2-4 Rue Vincent Van Gogh, 93360 Neuilly-Plaisance - France",
      href: "https://www.google.com/maps/place/NEOQUALIS/@48.8520389,2.5131129,17z",
    },
  ];

  const socialLinks = [
    {
      name: "LinkedIn",
      icon: Linkedin,
      url: "https://www.linkedin.com/company/neoqualis/",
      color: "hover:text-blue-500",
    },
    {
      name: "Instagram",
      icon: Instagram,
      url: "https://www.instagram.com/neoqualis/",
      color: "hover:text-pink-500",
    },
    {
      name: "Facebook",
      icon: Facebook,
      url: "https://www.facebook.com/profile.php?id=61576358416309",
      color: "hover:text-blue-600",
    }
  ];

  const links = {
    expertise: [
      { label: "Testing & QA", href: "/expertise#qa" },
      { label: "Business Analysis", href: "/expertise#ba" },
      { label: "Automatisation & CI/CD", href: "/expertise#automation" },
      { label: "Audit QA", href: "/audit" },
      { label: "Formation (Qualis-Academy)", href: "/expertise#other-services" },
    ],
    company: [
      { label: "Qui sommes-nous ?", href: "/about" },
      { label: "Nos valeurs", href: "/about#values" },
      { label: "RSE & Engagements", href: "/csr" },
    ],
    legal: [
      { label: "Mentions légales", href: "/legal" },
      { label: "Politique de confidentialité", href: "/privacy" },
      { label: "CGU", href: "/terms" },
    ],
  };

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <motion.div
            className="space-y-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <img
              src="/neoqualis_logo_white.png" 
              alt="Neoqualis"
              className="h-12"
            />
            <p className="text-gray-400 max-w-sm">
              ESN française spécialisée en Testing (QA) et Business Analysis.
              Nous sécurisons vos parcours digitaux avec rigueur et transparence.
            </p>
            <div className="flex items-center space-x-4">
              {socialLinks.map(({ name, icon: Icon, url, color }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-2 bg-gray-800 rounded-full transition-colors ${color}`}
                  aria-label={name}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Expertise Links */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-lg font-semibold mb-6 text-white">
              Nos Expertises
            </h3>
            <ul className="space-y-3">
              {links.expertise.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary transition-colors flex items-center group"
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company Links */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-lg font-semibold mb-6 text-white">
              L'entreprise
            </h3>
            <ul className="space-y-3">
              {links.company.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-primary transition-colors flex items-center group"
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-lg font-semibold mb-6 text-white">Contact</h3>
            <ul className="space-y-4">
              {contactInfo.map(({ icon: Icon, label, value, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    className="flex items-center text-gray-400 hover:text-primary transition-colors"
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-300">{label}</p>
                      <p className="text-sm">{value}</p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
            
            {/* ✅ Quick Contact CTA avec navigation et scroll optimisé */}
            <div className="mt-6 pt-6 border-t border-gray-800">
              <button
                onClick={handleContactClick}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-white text-primary font-medium shadow hover:bg-gray-100 transition-all duration-200 cursor-pointer hover:shadow-lg transform hover:-translate-y-0.5"
                aria-label="Aller au formulaire de contact"
              >
                Nous contacter
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} Neoqualis. Tous droits réservés.
              | ESN française spécialisée en Testing & Business Analysis
            </p>
            <ul className="flex space-x-6">
              {links.legal.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;